import { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  LayoutDashboard,
  Building2,
  CreditCard,
  BarChart3,
  Users,
  Settings,
  X,
  Shield,
  UserCog,
  Search,
  Loader2,
  LogOut,
  Headphones
} from 'lucide-react';
import { useImpersonation } from '../../contexts/ImpersonationContext';
import { useAuth } from '../../contexts/AuthContext';
import { adminApi, type Tenant } from '../../services/adminApi';
import { getOpenTicketCount } from '../../services/supportTicketApi';

interface AdminSidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const AdminSidebar = ({ isOpen, onClose }: AdminSidebarProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { startImpersonation, isLoading: impersonationLoading } = useImpersonation();
  const [showImpersonationModal, setShowImpersonationModal] = useState(false);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [tenantsLoading, setTenantsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTenant, setSelectedTenant] = useState<string | null>(null);
  const [impersonationReason, setImpersonationReason] = useState('');
  const [openTicketCount, setOpenTicketCount] = useState(0);

  useEffect(() => {
    loadOpenTicketCount();
    const interval = setInterval(loadOpenTicketCount, 60000);
    return () => clearInterval(interval);
  }, []);

  const loadOpenTicketCount = async () => {
    try {
      const count = await getOpenTicketCount();
      setOpenTicketCount(count);
    } catch (error) {
      console.error('Failed to load open ticket count:', error);
    }
  };

  const primaryNavItems: Array<{ label: string; icon: React.ElementType; path: string; badge?: number }> = [
    {
      label: t('admin.overview', 'Overview'),
      icon: LayoutDashboard,
      path: '/admin'
    },
    {
      label: t('admin.customers', 'Customers'),
      icon: Building2,
      path: '/admin/customers'
    },
    {
      label: t('admin.billing', 'Plans & Billing'),
      icon: CreditCard,
      path: '/admin/billing'
    },
    {
      label: t('admin.analytics', 'Platform Analytics'),
      icon: BarChart3,
      path: '/admin/analytics'
    },
    {
      label: t('admin.support', 'Support Center'),
      icon: Headphones,
      path: '/admin/support',
      badge: openTicketCount
    }
  ];

  const sharedNavItems = [
    {
      label: t('admin.allUsers', 'Users'),
      icon: Users,
      path: '/admin/users'
    },
    {
      label: t('admin.settings', 'Settings'),
      icon: Settings,
      path: '/admin/settings'
    }
  ];

  useEffect(() => {
    if (showImpersonationModal && tenants.length === 0) {
      loadTenants();
    }
  }, [showImpersonationModal, tenants.length]);

  const loadTenants = async () => {
    setTenantsLoading(true);
    try {
      const data = await adminApi.getAllTenants();
      setTenants(data);
    } catch (error) {
      console.error('Failed to load tenants:', error);
    } finally {
      setTenantsLoading(false);
    }
  };

  const handleStartImpersonation = async () => {
    if (!selectedTenant) return;

    const success = await startImpersonation(selectedTenant, impersonationReason || undefined);
    if (success) {
      setShowImpersonationModal(false);
      setSelectedTenant(null);
      setImpersonationReason('');
      navigate('/dashboard');
    }
  };

  const filteredTenants = tenants.filter(tenant =>
    tenant.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    tenant.domain?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/60 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      <aside
        className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-gray-950 border-r border-gray-800 transform transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
      >
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between px-4 py-4 border-b border-gray-800">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-blue-600 flex items-center justify-center">
                <Shield className="h-5 w-5 text-white" />
              </div>
              <div>
                <span className="text-lg font-bold text-white">{t('admin_sidebar.title')}</span>
                <span className="block text-xs text-gray-500">{t('admin_sidebar.mega_admin')}</span>
              </div>
            </div>
            <button
              onClick={onClose}
              className="lg:hidden p-1 rounded hover:bg-gray-800 text-gray-400"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <nav className="flex-1 overflow-y-auto py-4 px-3">
            <div className="mb-4">
              <p className="px-3 mb-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                {t('admin_sidebar.platform')}
              </p>
              <ul className="space-y-1">
                {primaryNavItems.map((item) => (
                  <li key={item.path}>
                    <NavLink
                      to={item.path}
                      end={item.path === '/admin'}
                      onClick={onClose}
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                          isActive
                            ? 'bg-blue-600 text-white'
                            : 'text-gray-400 hover:text-white hover:bg-gray-800'
                        }`
                      }
                    >
                      <item.icon className="h-5 w-5" />
                      <span className="flex-1">{item.label}</span>
                      {item.badge !== undefined && item.badge > 0 && (
                        <span className="px-2 py-0.5 text-xs font-medium bg-red-500 text-white rounded-full">
                          {item.badge}
                        </span>
                      )}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>

            <div className="border-t border-gray-800 pt-4">
              <p className="px-3 mb-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                {t('admin_sidebar.shared_tools')}
              </p>
              <ul className="space-y-1">
                {sharedNavItems.map((item) => (
                  <li key={item.path}>
                    <NavLink
                      to={item.path}
                      onClick={onClose}
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                          isActive
                            ? 'bg-blue-600 text-white'
                            : 'text-gray-400 hover:text-white hover:bg-gray-800'
                        }`
                      }
                    >
                      <item.icon className="h-5 w-5" />
                      {item.label}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          </nav>

          <div className="p-4 border-t border-gray-800 space-y-2">
            <button
              onClick={() => setShowImpersonationModal(true)}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium bg-amber-600/20 text-amber-400 hover:bg-amber-600/30 transition-colors"
            >
              <UserCog className="h-5 w-5" />
              {t('admin_sidebar.impersonate_tenant')}
            </button>
            <button
              onClick={async () => {
                await logout();
                navigate('/login');
              }}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium bg-red-600/20 text-red-400 hover:bg-red-600/30 transition-colors"
            >
              <LogOut className="h-5 w-5" />
              {t('common.logout', 'Logout')}
            </button>
          </div>
        </div>
      </aside>

      {showImpersonationModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/70"
            onClick={() => setShowImpersonationModal(false)}
          />
          <div className="relative bg-gray-900 rounded-xl shadow-2xl w-full max-w-md mx-4 border border-gray-700">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700">
              <div>
                <h3 className="text-lg font-semibold text-white">{t('admin_sidebar.impersonate_title')}</h3>
                <p className="text-sm text-gray-400">{t('admin_sidebar.impersonate_desc')}</p>
              </div>
              <button
                onClick={() => setShowImpersonationModal(false)}
                className="p-1 rounded hover:bg-gray-800 text-gray-400"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6">
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                <input
                  type="text"
                  placeholder={t('admin_sidebar.search_tenants')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="max-h-48 overflow-y-auto mb-4 rounded-lg border border-gray-700">
                {tenantsLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                  </div>
                ) : filteredTenants.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    {t('admin_sidebar.no_tenants')}
                  </div>
                ) : (
                  filteredTenants.map((tenant) => (
                    <button
                      key={tenant.id}
                      onClick={() => setSelectedTenant(tenant.id)}
                      className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                        selectedTenant === tenant.id
                          ? 'bg-blue-600/20 text-blue-400'
                          : 'text-gray-300 hover:bg-gray-800'
                      }`}
                    >
                      <div className="w-8 h-8 rounded-lg bg-gray-700 flex items-center justify-center">
                        <Building2 className="h-4 w-4 text-gray-400" />
                      </div>
                      <div>
                        <p className="font-medium">{tenant.name}</p>
                        {tenant.domain && (
                          <p className="text-xs text-gray-500">{tenant.domain}</p>
                        )}
                      </div>
                    </button>
                  ))
                )}
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  {t('admin_sidebar.reason_optional')}
                </label>
                <input
                  type="text"
                  placeholder={t('admin_sidebar.reason_placeholder')}
                  value={impersonationReason}
                  onChange={(e) => setImpersonationReason(e.target.value)}
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="bg-amber-900/20 border border-amber-700/50 rounded-lg p-3 mb-4">
                <p className="text-xs text-amber-400">
                  {t('admin_sidebar.audit_notice')}
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowImpersonationModal(false)}
                  className="flex-1 px-4 py-2 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleStartImpersonation}
                  disabled={!selectedTenant || impersonationLoading}
                  className="flex-1 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {impersonationLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                  {t('admin_sidebar.start_impersonation')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminSidebar;
